// src/pages/emision/DetalleRendicionModal.jsx
// Detalle de una rendición individual: cada certificado con su póliza asociada
// (si llegó) — valida visualmente la conexión certificado ↔ póliza.
import { useState, useEffect } from 'react';
import rendicionService from '../../services/rendicionService';

const ESTADO_CERT_PILL = {
  pendiente_recepcion: { bg: '#FBF1CC', color: '#8A5A0B', label: 'Pdte. recepción' },
  error_envio:         { bg: '#FEE2E2', color: '#B91C1C', label: 'Error envío' },
  emitido:             { bg: '#E1F1E7', color: '#0A7C46', label: 'Emitido' },
};

export default function DetalleRendicionModal({ rendicion, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg,  setErrMsg]  = useState(null);

  useEffect(() => {
    let vivo = true;
    rendicionService.getDetalle(rendicion.id)
      .then(d => { if (vivo) setData(d); })
      .catch(err => { if (vivo) setErrMsg(err.message); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [rendicion.id]);

  const certs     = data?.certificados ?? [];
  const conPoliza = certs.filter(c => c.poliza).length;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(14,17,22,.44)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: 'min(860px, 100%)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #EFEFEE', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.02em', margin: 0 }}>Detalle de rendición · certificados y pólizas</h2>
            <p style={{ fontSize: 12.5, color: '#8D8D8D', margin: '4px 0 0' }}>
              {rendicion.companias?.nombre_corto ?? 'Compañía'} · período {rendicion.periodo} · {conPoliza} de {certs.length} con póliza
            </p>
          </div>
          <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', fontSize: 20, color: '#8D8D8D', padding: 6, lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {errMsg && (
            <div style={{ margin: 16, padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, fontSize: 13, color: '#B91C1C' }}>
              {errMsg}
            </div>
          )}
          {loading ? (
            <p style={{ padding: '40px 24px', textAlign: 'center', color: '#A0A09E', fontSize: 13 }}>Cargando…</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#FAFAF9', borderBottom: '1px solid #EFEFEE' }}>
                  {['Certificado', 'Cliente', 'Plan', 'Estado emisión', 'N° Póliza', 'Vigencia', 'Recibida'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: '#A0A09E', whiteSpace: 'nowrap', background: '#FAFAF9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {certs.map(c => {
                  const pill = ESTADO_CERT_PILL[c.estado_emision] ?? { bg: '#ECECEA', color: '#6B6F72', label: c.estado_emision ?? '—' };
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #F3F3F2' }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'ui-monospace,monospace', fontSize: 11.5, color: '#54585A', whiteSpace: 'nowrap' }}>{c.codigo}</td>
                      <td style={{ padding: '10px 14px', color: '#1A1A1A' }}>{c.cliente_nombre}</td>
                      <td style={{ padding: '10px 14px', color: '#54585A' }}>{c.plan_nombre}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '2px 9px', background: pill.bg, color: pill.color, whiteSpace: 'nowrap' }}>{pill.label}</span>
                      </td>
                      <td style={{ padding: '10px 14px', fontFamily: 'ui-monospace,monospace', fontSize: 11.5, fontWeight: 600, color: c.poliza ? '#007D6E' : '#C4C4C2' }}>
                        {c.poliza?.numero_poliza ?? '—'}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#8D8D8D', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                        {c.poliza?.fecha_inicio ? `${c.poliza.fecha_inicio} → ${c.poliza.fecha_fin ?? '—'}` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#8D8D8D', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                        {c.poliza?.fecha_recepcion?.slice(0, 10) ?? '—'}
                      </td>
                    </tr>
                  );
                })}
                {!certs.length && !errMsg && (
                  <tr>
                    <td colSpan={7} style={{ padding: '40px 20px', textAlign: 'center', color: '#A0A09E', fontSize: 13 }}>
                      Sin certificados individuales para esta compañía
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #EFEFEE', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 9, border: 'none', cursor: 'pointer', background: '#F1F1EF', color: '#54585A' }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
